import { useEffect, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, TextField,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import { useGenerateIotQrBatch } from '../../api/queries';

/**
 * "Generate IoT QR batch" — minimal dialog for ESP32 Deployment Plan §3 M1.
 * Just code + count + optional notes. Default layout is 24-per-A4 (Avery
 * L7159 — the labels are small because the HAT only carries one sticker).
 */
export function GenerateIotQrBatchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useGenerateIotQrBatch();

  const [code, setCode]   = useState('');
  const [count, setCount] = useState<number>(50);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setCode(''); setCount(50); setNotes(''); setError(null);
      create.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const valid = code.trim().length > 0 && count > 0 && count <= 10_000;

  async function submit() {
    setError(null);
    try {
      await create.mutateAsync({
        code:           code.trim(),
        notes:          notes.trim() || null,
        count,
        labelPaper:     'A4',
        labelWidthMm:   70,
        labelHeightMm:  25,
        colsPerSheet:   3,
        rowsPerSheet:   8,
      });
      onClose();
    } catch (e) {
      // Surface API failure text per the "errors visible in UI" rule —
      // no silent failures, never an empty Alert.
      const msg = e instanceof Error ? e.message : String(e);
      setError(`Generate failed: ${msg}`);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate IoT QR batch</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <ErrorAlert title="Generate failed" message={error} />}
          <TextField
            label="Batch code"
            placeholder="IOT-2026-05-001"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            fullWidth required
            helperText="Human-readable label, must be unique across batches."
          />
          <TextField
            label="Count"
            type="number"
            value={count}
            onChange={(e) => setCount(Math.max(0, parseInt(e.target.value || '0', 10)))}
            fullWidth required
            inputProps={{ min: 1, max: 10000 }}
            helperText="Between 1 and 10,000 IoT QR pairs."
          />
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth multiline minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!valid || create.isPending}>
          {create.isPending ? 'Generating…' : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
